import type {
	CommandConstructor,
	CommandProperties,
	SerializableValue,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

import type { NativeEmbedChildCommand, NativeEmbedCommand } from '../../../common/types';
import type { NativeEmbedFeature } from '../../../common/types/features/main';

export interface NativeEmbedCommandParams extends CommandProperties, NativeEmbedCommand {}

export interface NativeEmbedChildCommandParams extends CommandProperties, NativeEmbedChildCommand {}

export const NativeEmbedBridgeCommand: CommandConstructor<
	NativeEmbedCommandParams,
	SerializableValue
> = createCommand<NativeEmbedCommandParams>('native-embed-command');

export const NativeEmbedChildBridgeCommand: CommandConstructor<
	NativeEmbedChildCommandParams,
	SerializableValue
> = createCommand<NativeEmbedChildCommandParams>('native-embed-child-command');

/**
 * Command params for the feature handshake command.
 */
export interface NativeEmbedFeatureHandshakeCommandParams extends CommandProperties {
	features: NativeEmbedFeature[];
}

export const NativeEmbedFeatureHandshakeCommand: CommandConstructor<
	NativeEmbedFeatureHandshakeCommandParams,
	SerializableValue
> = createCommand<NativeEmbedFeatureHandshakeCommandParams>(
	'native-embed-feature-handshake-command',
);

/**
 * Command params for selected-change events (parent → child).
 * Emitted when the embed's selection state changes.
 */
export interface NativeEmbedSelectedChangeCommandParams extends CommandProperties {
	selected: boolean;
}

export const NativeEmbedSelectedChangeCommand: CommandConstructor<
	NativeEmbedSelectedChangeCommandParams,
	SerializableValue
> = createCommand<NativeEmbedSelectedChangeCommandParams>('native-embed-selected-change-command');

/**
 * View mode for the embed container (parent → child).
 */
export type NativeEmbedViewMode = 'regular' | 'maximize';

/**
 * Command params for view-mode-change events (parent → child).
 * Emitted when the embed's view mode changes (e.g. full screen / maximized vs regular).
 */
export interface NativeEmbedViewModeChangeCommandParams extends CommandProperties {
	viewMode: NativeEmbedViewMode;
}

export const NativeEmbedViewModeChangeCommand: CommandConstructor<
	NativeEmbedViewModeChangeCommandParams,
	SerializableValue
> = createCommand<NativeEmbedViewModeChangeCommandParams>('native-embed-view-mode-change-command');

/**
 * Command params for click events forwarded from child to host
 * when notify-host-click is enabled.
 */
export interface NativeEmbedClickEventCommandParams extends CommandProperties {}

export const NativeEmbedClickEventCommand: CommandConstructor<
	NativeEmbedClickEventCommandParams,
	SerializableValue
> = createCommand<NativeEmbedClickEventCommandParams>('native-embed-click-event-command');

/**
 * Command params for always-show-title-change events (parent → child).
 * Emitted when the user toggles the always-show-title option.
 */
export interface NativeEmbedAlwaysShowTitleChangeCommandParams extends CommandProperties {
	alwaysShowTitle: boolean;
}

export const NativeEmbedAlwaysShowTitleChangeCommand: CommandConstructor<
	NativeEmbedAlwaysShowTitleChangeCommandParams,
	SerializableValue
> = createCommand<NativeEmbedAlwaysShowTitleChangeCommandParams>(
	'native-embed-always-show-title-change-command',
);

/**
 * Command params for toggle-full-screen requests forwarded from child to host
 * when toggle-full-screen is enabled.
 */
export interface NativeEmbedToggleFullScreenCommandParams extends CommandProperties {}

export const NativeEmbedToggleFullScreenCommand: CommandConstructor<
	NativeEmbedToggleFullScreenCommandParams,
	SerializableValue
> = createCommand<NativeEmbedToggleFullScreenCommandParams>(
	'native-embed-toggle-full-screen-command',
);

/**
 * Command params for resize requests forwarded from child to host.
 * The child sends its desired pixel dimensions; the host persists them
 * to the ADF node parameters.
 */
export interface NativeEmbedRequestResizeCommandParams extends CommandProperties {
	height?: number;
	width?: number;
}

export const NativeEmbedRequestResizeCommand: CommandConstructor<
	NativeEmbedRequestResizeCommandParams,
	SerializableValue
> = createCommand<NativeEmbedRequestResizeCommandParams>('native-embed-request-resize-command');

/**
 * Command params for row-boundary updates pushed from child → host.
 *
 * `boundaries` is an ascending list of Y positions (in child-iframe content
 * pixels) marking the top edge of each row plus the bottom edge of the last
 * row, i.e. N+1 entries for N rows. Sent reactively whenever the child's row
 * layout changes (only for database native embeds).
 */
export interface NativeEmbedSendRowBoundariesCommandParams extends CommandProperties {
	boundaries: number[];
}

export const NativeEmbedSendRowBoundariesCommand: CommandConstructor<
	NativeEmbedSendRowBoundariesCommandParams,
	SerializableValue
> = createCommand<NativeEmbedSendRowBoundariesCommandParams>(
	'native-embed-send-row-boundaries-command',
);
